import { StorageManager } from '@vue-storefront/core/lib/storage-manager'
import { isServer } from '@vue-storefront/core/helpers'
import EventBus from '@vue-storefront/core/compatibility/plugins/event-bus'
import Cookies from 'js-cookie'
import { STORAGE } from '..'

export const MARKETING_GROUP_ID = 2
export const ANALYTICAL_GROUP_ID = 3

export const MARKETING_CONSENT = 'marketing'
export const ANALYTICAL_CONSENT = 'analytical'

export type ConsentType = typeof MARKETING_CONSENT | typeof ANALYTICAL_CONSENT

export const CONSENT_VERSION = 2

export const CONSENT_STORAGE_KEY = 'gdprCookieSetup'
export const CONSENT_GROUP_IDS_KEY = 'cookeGroupIds'
export const CONSENT_VERSION_KEY = 'version'
export const CONSENT_UPDATED_EVENT = 'gdpr-consent-updated'
export const ADS_ENABLED_COOKIE = 'ads_cookies_enabled'
export const COOKIE_POLICY_PATH = '/i/cookie-policy'

const GTAG_CONSENT_COMMAND = 'consent'
const CONSENT_ACTION_DEFAULT = 'default'
const CONSENT_ACTION_UPDATE = 'update'

const GRANTED = 'granted'
const DENIED = 'denied'

const MARKETING_CONSENT_KEYS = ['ad_storage', 'ad_user_data', 'ad_personalization']
const ANALYTICAL_CONSENT_KEYS = ['analytics_storage']

const DEFAULT_CONSENT = {
  'ad_storage': DENIED,
  'ad_user_data': DENIED,
  'ad_personalization': DENIED,
  'analytics_storage': DENIED,
  'functionality_storage': GRANTED,
  'personalization_storage': DENIED,
  'security_storage': GRANTED
}

const COOKIE_PATH = '/'
const COOKIE_PATTERN_WILDCARD = '*'
const DOMAIN_SEPARATOR = '.'

const GROUP_COOKIE_PATTERNS: { [groupId: number]: string[] } = {
  [MARKETING_GROUP_ID]: [
    '__kla_id', 'W_GUID', 'W_LMT', '_tpapp', '_gcl_*', '_gac_*',
    'FPAU', 'FPGCLAW', 'FPGCLDC'
  ],
  [ANALYTICAL_GROUP_ID]: [
    '_clck', '_clsk', 'ph_*', '__ph_*', '_ga', '_ga_*', '_gid', '_gat*',
    'FPLC'
  ]
}

const GROUP_STORAGE_COLLECTIONS: { [groupId: number]: string[] } = {
  [MARKETING_GROUP_ID]: ['klaviyo']
}

const ANALYTICAL_STORAGE_PREFIXES = ['ph_', '__ph_']

/**
 * @param {String} action
 * @param {Record<String, String>} props
 * @return {void}
 */
const pushConsent = (action: string, props: Record<string, string>): void => {
  if (isServer) {
    return
  }

  /**
   * Here we do not use global gtag as it should not
   * be yet initialized to make sure default are set before
   */
  (window as any).dataLayer = (window as any).dataLayer || [];

  const gtag = function (_command: string, _action: string, _params?: Record<string, string>): void {
    (window as any).dataLayer.push(arguments);
  }

  gtag(GTAG_CONSENT_COMMAND, action, props)
}

/**
 * @return {void}
 */
const setDefaultConsent = (): void => {
  pushConsent(CONSENT_ACTION_DEFAULT, DEFAULT_CONSENT)
}

/**
 * @param {Boolean} marketingGranted
 * @param {Boolean} analyticsGranted
 * @return {Record<String, String>}
 */
const buildConsentUpdate = (marketingGranted: boolean, analyticsGranted: boolean): Record<string, string> => {
  const update: Record<string, string> = {}

  MARKETING_CONSENT_KEYS.forEach(key => {
    update[key] = marketingGranted ? GRANTED : DENIED
  })

  ANALYTICAL_CONSENT_KEYS.forEach(key => {
    update[key] = analyticsGranted ? GRANTED : DENIED
  })

  return update
}

/**
 * @param {Number[]} groupIds
 * @return {Object}
 */
const buildConsentRecord = (groupIds: number[]): object => {
  return {
    [CONSENT_GROUP_IDS_KEY]: groupIds,
    [CONSENT_VERSION_KEY]: CONSENT_VERSION
  }
}

/**
 * @param {*} record
 * @return {Boolean}
 */
const isCurrentConsentRecord = (record: any): boolean => {
  return !!record &&
    !Array.isArray(record) &&
    record[CONSENT_VERSION_KEY] === CONSENT_VERSION &&
    Array.isArray(record[CONSENT_GROUP_IDS_KEY])
}

/**
 * @return {Promise<*>}
 */
const readConsentRecord = async (): Promise<any> => {
  if (isServer) {
    return null
  }

  try {
    return await StorageManager.get(STORAGE).getItem(CONSENT_STORAGE_KEY)
  } catch (error) {
    console.error('Error while reading stored consent:', error);

    return null
  }
}

/**
 * @return {Promise<Boolean>}
 */
const hasConsentChoice = async (): Promise<boolean> => {
  return isCurrentConsentRecord(await readConsentRecord())
}

/**
 * @return {Promise<Number[]>}
 */
const consentedGroupIds = async (): Promise<number[]> => {
  const record = await readConsentRecord()

  if (!isCurrentConsentRecord(record)) {
    return []
  }

  return record[CONSENT_GROUP_IDS_KEY]
    .map(Number)
    .filter(groupId => !isNaN(groupId))
}

/**
 * @param {Number[]} groupIds
 * @return {Promise<void>}
 */
const storeConsentChoice = async (groupIds: number[]): Promise<void> => {
  await StorageManager.get(STORAGE).setItem(CONSENT_STORAGE_KEY, buildConsentRecord(groupIds))
}

/**
 * @param {ConsentType} type
 * @return {Number}
 */
const groupIdForConsentType = (type: ConsentType): number => {
  return type === MARKETING_CONSENT ? MARKETING_GROUP_ID : ANALYTICAL_GROUP_ID
}

/**
 * @param {ConsentType} type
 * @return {Promise<Boolean>}
 */
const isAccepted = async (type: ConsentType): Promise<boolean> => {
  return (await consentedGroupIds()).includes(groupIdForConsentType(type))
}

/**
 * @param {Number[]} groupIds
 * @return {void}
 */
const applyConsent = (groupIds: number[]): void => {
  pushConsent(CONSENT_ACTION_UPDATE, buildConsentUpdate(
    groupIds.includes(MARKETING_GROUP_ID),
    groupIds.includes(ANALYTICAL_GROUP_ID)
  ))
}

/**
 * @return {Promise<void>}
 */
const updateConsent = async (): Promise<void> => {
  if (isServer) {
    return
  }

  applyConsent(await consentedGroupIds())
}

/**
 * @return {void}
 */
const notifyConsentUpdated = (): void => {
  if (!isServer) {
    EventBus.$emit(CONSENT_UPDATED_EVENT)
  }
}

/**
 * @param {ConsentType} type
 * @param {Function} onGranted
 * @return {Promise<void>}
 */
const whenConsented = async (type: ConsentType, onGranted: () => void | Promise<void>): Promise<void> => {
  if (isServer) {
    return
  }

  let started = false
  let queue: Promise<void> = Promise.resolve()

  const attempt = async (): Promise<void> => {
    if (started || !await isAccepted(type)) {
      return
    }

    await onGranted()

    started = true
  }

  const start = (): Promise<void> => {
    queue = queue
      .then(attempt)
      .then(() => {
        if (started) {
          EventBus.$off(CONSENT_UPDATED_EVENT, start)
        }
      })
      .catch(error => {
        console.error('Consented module failed to start:', error);
      })

    return queue
  }

  EventBus.$on(CONSENT_UPDATED_EVENT, start)

  await start()
}

/**
 * @param {String} name
 * @param {String} pattern
 * @return {Boolean}
 */
const matchesCookiePattern = (name: string, pattern: string): boolean => {
  return pattern.endsWith(COOKIE_PATTERN_WILDCARD)
    ? name.startsWith(pattern.slice(0, -COOKIE_PATTERN_WILDCARD.length))
    : name === pattern
}

/**
 * @param {String} name
 * @return {void}
 */
const removeCookie = (name: string): void => {
  if (isServer) {
    return
  }

  const { hostname } = window.location
  const parentDomain = hostname.split(DOMAIN_SEPARATOR).slice(1).join(DOMAIN_SEPARATOR)

  const scopes: { path: string, domain?: string }[] = [
    { path: COOKIE_PATH },
    { path: COOKIE_PATH, domain: hostname },
    { path: COOKIE_PATH, domain: `${DOMAIN_SEPARATOR}${hostname}` }
  ]

  if (parentDomain.includes(DOMAIN_SEPARATOR)) {
    scopes.push({ path: COOKIE_PATH, domain: `${DOMAIN_SEPARATOR}${parentDomain}` })
  }

  scopes.forEach(scope => Cookies.remove(name, scope))
}

/**
 * @return {void}
 */
const removeAdsEnabledCookie = (): void => {
  removeCookie(ADS_ENABLED_COOKIE)
}

/**
 * @param {Number[]} grantedGroupIds
 * @param {Object} groupMap
 * @return {String[]}
 */
const revokedEntriesFor = (grantedGroupIds: number[], groupMap: { [groupId: number]: string[] }): string[] => {
  return Object.keys(groupMap)
    .map(Number)
    .filter(groupId => !grantedGroupIds.includes(groupId))
    .reduce((entries, groupId) => entries.concat(groupMap[groupId]), [] as string[])
}

/**
 * @param {Storage} storage
 * @return {void}
 */
const purgeAnalyticalWebStorage = (storage: Storage): void => {
  Object.keys(storage)
    .filter(key => ANALYTICAL_STORAGE_PREFIXES.some(prefix => key.startsWith(prefix)))
    .forEach(key => storage.removeItem(key))
}

/**
 * @param {Number[]} grantedGroupIds
 * @return {Promise<void>}
 */
const purgeRevokedStorage = async (grantedGroupIds: number[]): Promise<void> => {
  if (isServer) {
    return
  }

  const revokedPatterns = revokedEntriesFor(grantedGroupIds, GROUP_COOKIE_PATTERNS)

  Object.keys(Cookies.get()).forEach(name => {
    if (revokedPatterns.some(pattern => matchesCookiePattern(name, pattern))) {
      removeCookie(name)
    }
  })

  if (!grantedGroupIds.includes(ANALYTICAL_GROUP_ID)) {
    purgeAnalyticalWebStorage(window.localStorage)
    purgeAnalyticalWebStorage(window.sessionStorage)
  }

  await Promise.all(
    revokedEntriesFor(grantedGroupIds, GROUP_STORAGE_COLLECTIONS).map(async collection => {
      try {
        await StorageManager.get(collection).clear()
      } catch (error) {
        console.error(`Failed to clear the ${collection} storage collection:`, error);
      }
    })
  )
}

export {
  setDefaultConsent,
  applyConsent,
  updateConsent,
  storeConsentChoice,
  notifyConsentUpdated,
  whenConsented,
  isAccepted,
  hasConsentChoice,
  consentedGroupIds,
  purgeRevokedStorage,
  removeAdsEnabledCookie
}
